import React, { useMemo } from 'react';
import { orderBy, uniqBy } from 'lodash';
import { useRequest } from 'hooks';
import DayJs from 'utils/Dayjs';
import Document from 'documents';

import Avatar from 'components/Misc/Avatar';
import { Button } from 'components/Misc/Form';
import { Title } from 'components/Misc/Typography';

import State from 'containers/Administrator/Grades/Profile/Reports/State';
import Subjects from 'containers/Administrator/Grades/Profile/Reports/Subjects';
import Settings from 'containers/Administrator/Grades/Profile/Reports/Settings';

import { useGradeProfileStore } from 'store/grade/useGradeProfileStore';
import { useGradeReportStore } from 'store/grade/useGradeReportStore';

const Student = ({ student }) => {
	// Use grade's profile store
	const [grade, program] = useGradeProfileStore(state => [state.grade, state.program]);
	const onUpdateReport = useGradeReportStore(state => state.onUpdateReport);

	// Store request
	const [[progress], request] = useRequest('post', 'mail');
	
	// Filter subject's from marks
	const subjects = useMemo(() => {
		const all = student.marks?.reduce((result, mark) => {
		 return result.some(subject => subject.id === mark.subjectId)
				? result
				: [...result, mark.subject];
		}, []);

		// Remove duplicates and sort results by subject's order
		const unique = orderBy(uniqBy(all, 'id'), 'order');

		// Append marks and hour count
		return unique.map(subject => {
			// Calculate hours in subject
			const hours = student.coursesrel.reduce((total, rel) => {
				if (rel.course.subjectId === subject.id) {
					return total + rel.course.hours;
				}

				return total;
			}, 0);

			// Find final or last middle mark
			const mark = student.marks.find(m => {
				if (m.subjectId === subject.id && (m.type === 'final' || m.type === 'middle')) {
					return m;
				}
			});

			return {
				...subject,
				hours,
				mark
			};
		});
	}, [student]);

	// Handle report download
	const onDownloadReport = () => {
		if ([7,8,9].includes(grade.number)) {
			Document('report-middle', {
				action: 'download',
				noSignature: false
			}, student, subjects, program, grade);
		} else {
			Document('report-high', {
				action: 'download',
				noSignature: false
			}, student, subjects, program, grade);
		}
	};

	// Handle report printing
	const onPrintReport = () => {
		if ([7,8,9].includes(grade.number)) {
			Document('report-middle', {
				action: 'print',
				noSignature: false
			}, student, subjects, program, grade);
		} else {
			Document('report-high', {
				action: 'print',
				noSignature: false
			}, student, subjects, program, grade);
		}
	};

	// Handle report mail sending
	const onMailReport = async () => {
		// Get document's base64 string
		const base64 = [7,8,9].includes(grade.number)
			? await Document('report-middle', {
				action: 'base64',
				noSignature: false
			}, student, subjects, program, grade)
			: await Document('report-high', {
				action: 'base64',
				noSignature: false
			}, student, subjects, program, grade);

		// Mail generated report to student's email
		const [success] = await request({
			studentId: student.id,
			gradeId: grade.id,
			report: base64
		}, {
			type: 'student:report'
		});

		// Update report's data
		if (success) onUpdateReport(success.report);
	};

	return (
		<div className="p-4 space-y-2">
			{/* Student's data */}
			<div className="flex items-center space-x-2">
				<div className="flex flex-1 items-center space-x-2">
					<Avatar
						small
						src={student.avatar}
						alt={student.name}
						online={student.online}
					/>

					<Title
						title={`${student.name} ${student.surname}`}
						className="flex-1"
					/>
				</div>

				<Button
					small
					square
					icon="link"
					to={`/users/${student.id}`}
				/>
			</div>

			{/* Report's data */}
			<div className="py-2 grid grid-cols-2 gap-4">
				<div className="flex flex-col">
					<div className="mb-1 text-sm font-bold text-neutral-800 dark:text-white transition-colors">
						Mācību gads:
					</div>

					<div className="">
						{DayJs(student.graderel.startDate).format('YYYY')}./{DayJs(student.graderel.endDate).format('YYYY')}. mācību gads
					</div>
				</div>

				<div className="flex flex-col">
					<div className="mb-1 text-sm font-bold text-neutral-800 dark:text-white transition-colors">
						Klase:
					</div>

					<div className="">
						{grade.title}
					</div>
				</div>
			
				<div className="flex flex-col">
					<div className="mb-1 text-sm font-bold text-neutral-800 dark:text-white transition-colors">
						Izglītības programma:
					</div>

					<div className="">
						{program.title}
					</div>
				</div>

				<div className="flex flex-col">
					<div className="mb-1 text-sm font-bold text-neutral-800 dark:text-white transition-colors">
						Izglītības programmas kods:
					</div>

					<div className="">
						{program.contractNumber}
					</div>
				</div>
			</div>

			{/* Student's subjects */}
			<Subjects subjects={subjects} />

			{/* Student's report parameters */}
			<Settings studentId={student.id} report={student.report} />

			{/* Student's grade state */}
			<State studentId={student.id} grade={student.graderel} />
			
			{/* Print, download and send report */}
			<div className="flex items-end gap-2">
				<div className="flex-1 flex flex-col">
					<div className="mb-1 text-sm font-bold text-neutral-800 dark:text-white transition-colors">
						Liecība nosūtīta:
					</div>

					<div className="">
						{student.report.issuedAt ? DayJs(student.report.issuedAt).format('LLLL') : 'Nav'}
					</div>
				</div>

				<div className="flex gap-2">
					<Button
						square
						icon="download"
						onClick={onDownloadReport}
					/>

					<Button
						square
						icon="print"
						onClick={onPrintReport}
					/>

					<Button
						square
						icon="mail"
						progress={progress}
						onClick={onMailReport}
					/>
				</div>
			</div>
		</div>
	);
};

export default Student;